<template>
	<div class="icon-picker flex-centerize" :class="{ [`icon-picker-${id}`]: true, 'is-open': isShowPicker }">
		<div class="selection-bar icon-selector flex-centerize">
			<div v-for="(iconOption, index) in iconOptionsForSelectionBar" :key="index">
				<v-btn class="icon-selector-btn" :class="{ selected: isSelected(iconOption) }" :disabled="readonly" @click="selectIcon(iconOption)">
					<SIcon :name="iconOption.name" v-if="iconOption.custom" />
					<v-icon class="menu_icon" v-else>{{ iconOption.name }}</v-icon>
				</v-btn>
			</div>
		</div>
		<v-menu v-if="!readonly" v-model="isShowPicker" bottom content-class="picker-layout" :close-on-content-click="false">
			<template #activator="{ on, attrs }">
				<div v-on="on" v-bind="attrs" class="toggle-button-container">
					<div class="toggle-button hover-effect"><SIcon :name="'three-dots-button'" /></div>
				</div>
			</template>

			<div class="picker-header">
				<div>{{ title }}</div>
				<div @click="isShowPicker = false" class="close-x"></div>
			</div>
			<div class="dropdown-section">
				<v-row no-gutters>
					<v-col cols="12" class="my-2">
						<v-text-field
							v-model="searchText"
							placeholder="Search icon"
							class="search-input"
							dense
							single-line
							hide-details
							outlined
							append-icon="mdi-magnify"
						>
						</v-text-field>
					</v-col>
					<v-col cols="12">
						<v-row no-gutters justify="space-between" class="icon-selector-box icon-selector" @scroll="handleScroll">
							<v-col cols="auto" v-for="icon in filteredIcons" :key="icon.name">
								<v-lazy :options="{ threshold: 0.5 }">
									<div class="">
										<v-btn @click="selectIcon(icon)" :class="{ 'icon-selector-btn': true, selected: isSelected(icon) }">
											<SIcon :name="icon.name" v-if="icon.custom" />
											<v-icon class="menu_icon" v-else>{{ icon.name }}</v-icon>
										</v-btn>
									</div>
								</v-lazy>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</div>
		</v-menu>
	</div>
</template>

<script>
import { debounce } from 'lodash';
import { v4 as uuid } from 'uuid';

import { DEFAULT_ICONS } from './button-editor.const';
import { api } from '@/api/clients';

const transparentIcon = { name: 'ply-element-transparent', mdi: null, local: true, custom: true };
export default {
	name: 'IconPicker',
	components: {},
	props: {
		title: { type: String, default: '' },
		iconStyle: { type: Object },
		enableTransparent: { type: Boolean, default: false },
		readonly: { type: Boolean, default: true },
	},
	computed: {
		filteredIcons() {
			const { amountIconsToLoad } = this;
			if (!this.searchText) {
				const iconList = this.iconsList.filter((icon) => icon.name !== 'ply-element-default-icon');
				return [...this.defaultIcons, ...iconList].slice(0, amountIconsToLoad);
			}
			return this.iconsList.filter((icon) => icon.name.toLowerCase().includes(this.searchText.toLowerCase())).splice(0, amountIconsToLoad);
		},
		localIconStyle: {
			set(value) {
				this.$emit('update:iconStyle', { ...this.iconStyle, ...value });
			},
			get() {
				return this.iconStyle;
			},
		},
		defaultIcons() {
			return this.mappedDefaultIcons.filter((icon) => DEFAULT_ICONS.includes(icon.mdi) || icon.custom);
		},
		mappedDefaultIcons() {
			return this.iconsData.icons.map((iconData) => ({ ...iconData, ...{ custom: false, local: false } }));
		},
	},
	watch: {
		iconStyle: {
			handler() {
				this.setIconsSelectionBar();
			},
			deep: true,
		},
	},

	methods: {
		selectIcon(icon) {
			this.localIconStyle = icon;
			this.isShowPicker = false;
			this.$nextTick(() => this.setIconsSelectionBar());
		},
		setIconsSelectionBar() {
			let selectedIcons;
			if (!this.localIconStyle.name || ['ply-element-transparent', 'ply-element-default-icon'].includes(this.localIconStyle.name)) {
				selectedIcons = this.iconOptionsForSelectionBar;
			} else {
				const isInOptionalIcons = this.iconOptionsForSelectionBar.some((odi) => odi.name === this.localIconStyle.name);
				selectedIcons = isInOptionalIcons
					? this.iconOptionsForSelectionBar
					: [transparentIcon, this.localIconStyle, ...this.iconOptionsForSelectionBar.splice(1, 4)];
			}
			this.iconOptionsForSelectionBar = [transparentIcon, ...selectedIcons.slice(1, 5)];
		},
		isSelected(icon) {
			return (
				icon &&
				this.iconStyle &&
				((this.iconStyle.name && icon.name === this.iconStyle.name) || (this.iconStyle.mdi && icon.mdi === this.iconStyle.mdi))
			);
		},
		handleScroll(el) {
			const debounced = debounce(() => (this.amountIconsToLoad += 20), 500);
			if (el.srcElement.offsetHeight + el.srcElement.scrollTop >= el.srcElement.scrollHeight) {
				debounced();
			}
		},
		async getIcons() {
			return api.get(`${process.env.VUE_APP_APP_URL}assets/json/icons.json`);
		},
	},
	async created() {
		this.isLoading = true;
		try {
			this.iconsData = await this.getIcons();
			this.localIconStyle = this.iconStyle;
			this.iconsList = this.mappedDefaultIcons
				.map((iconData) => ({ ...iconData, ...{ custom: false, local: false } }))
				.filter((icon) => !DEFAULT_ICONS.includes(icon.mdi) && !icon.custom);
			this.iconOptionsForSelectionBar = [...this.defaultIcons.slice(0, 5)];
		} catch (e) {
			this.$toast.error('Failed to load icons');
		} finally {
			this.isLoading = false;
		}
	},
	data() {
		return {
			id: uuid(),
			isShowPicker: false,
			searchText: null,
			iconsList: [],
			iconOptionsForSelectionBar: [],
			amountIconsToLoad: 53,
			isLoading: false,
			iconsData: { icons: [] },
		};
	},
};
</script>

<style scoped lang="scss">
@import '@/styles/utility-classes/picker-layout.scss';
.picker-layout {
	width: 400px;
	padding: 10px;
	margin: 10px 0 0 0;
	border-radius: 15px;
	box-shadow: 0px 11px 28px #00000058;
	.picker-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 4px 7px !important;
		font-size: 13px !important;
		color: var(--third-text-color) !important;
	}
	.icon-selector-box {
		padding: 2px;
		height: 125px;
		overflow-y: auto;
		overflow-x: hidden;
		background-color: var(--bg-color);
		border: var(--primary-border);
		border-radius: var(--border-radius);

		.col {
			margin: 12px;
		}
	}
	.search-input {
		// max-width: calc(100% - 25px);
		font-size: 12px !important;
		font-weight: 400 !important;
		color: rgba(114, 124, 145, 0.85) !important;
		letter-spacing: 0.01em !important;
		text-indent: 3px !important;

		::v-deep {
			fieldset {
				background-color: var(--bg-color);
				border: var(--primary-border);
			}
		}
		& input {
			padding: 1px 8px 4px;

			.v-input__control {
				background: #21273e !important;
				height: 22px;
				border: var(--primary-border);
			}
			i {
				font-size: 16px !important;
				width: 20px !important;
				top: -5px;
				right: 10px;
			}
		}
	}
}

.icon-selector {
	&::after {
		/* fill last row */
		content: '';
		flex: auto;
	}
	.icon-selector-btn {
		$size: 12px;

		padding: $size;
		min-width: $size !important;
		width: $size !important;
		height: $size !important;
		border: 2px solid transparent;
		background-color: transparent !important;
	}

	.icon-selector-btn:hover,
	.selected {
		background-color: #e5effb !important;
		border-color: var(--shadow-color);
	}
}

::v-deep .icon-selector .v-icon {
	font-size: 14px;
}
::v-deep .v-btn--is-elevated {
	box-shadow: none;
}

.icon-picker {
	$height: 36px;

	position: relative;
	margin: 28px 0 0;
	z-index: $z-index-2;

	&.is-open {
		z-index: $z-index-3;
	}

	.selection-bar {
		max-width: 160px;
		margin: 0 4px 0 0;
		padding: 3px;
		border: var(--primary-border);
		border-radius: var(--border-radius);
		background-color: var(--bg-color);
	}

	.selection-bar {
		> div:first-child::after {
			content: '';
			height: 90%;
			margin: 0 6px;
			flex: auto;
			border-left: 2px solid var(--secondary-text-color);
		}
	}

	.toggle-button-container {
		position: relative;
		height: $height !important;
		width: $height !important;
		transition: all 0.15s;
		border: none;
		overflow: visible;
		box-sizing: border-box;

		.toggle-button {
			position: absolute;
			top: 0 !important;
			right: 0 !important;
			height: $height !important;
			width: $height !important;

			> :nth-child(1) {
				margin: 0 0 15px 0;
			}
			> :nth-child(2) {
				transform: translate(-3px, 2px);
			}

			::v-deep svg {
				height: 100%;
				width: 100%;
			}
		}
	}

	::v-deep svg path {
		fill: var(--secondary-text-color);
	}
}
</style>
